import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    AnalyticsState,
    GetDashboardPayload,
    GetEventRecordsPayload,
    GetTenantPayload,
} from "../types/analytics";
import {
    IAnalyticsSummaryResponse,
    IEventRecordsResponse,
    ITenantRanking,
    ITopEvent,
    ITrendPoint,
} from "../../interfaces/analytics.interface";

const emptyPanel = { data: undefined, loading: false, success: false, error: false };

const initialState: AnalyticsState = {
    organizationsMap: { ...emptyPanel },
    dashboardSummary: { ...emptyPanel },
    dashboardTenants: { ...emptyPanel },
    dashboardEvents: { ...emptyPanel },
    dashboardTrends: { ...emptyPanel },
    tenantSummary: { ...emptyPanel },
    tenantEvents: { ...emptyPanel },
    tenantTrends: { ...emptyPanel },
    eventRecords: { ...emptyPanel },
};

const loadingPanel = { data: undefined, loading: true, success: false, error: false };
const successPanel = <T,>(data: T) => ({ data, loading: false, success: true, error: false });
const failurePanel = { data: undefined, loading: false, success: false, error: true };

const analyticsSlice = createSlice({
    name: "analytics",
    initialState,
    reducers: {
        getAllOrganizationsForAnalytics(state) {
            state.organizationsMap = { ...loadingPanel };
        },
        getAllOrganizationsForAnalyticsSuccess(state, action: PayloadAction<Record<string, string>>) {
            state.organizationsMap = successPanel(action.payload);
        },
        getAllOrganizationsForAnalyticsFailure(state) {
            state.organizationsMap = { ...failurePanel };
        },

        getDashboardSummary(state, _action: PayloadAction<GetDashboardPayload>) {
            state.dashboardSummary = { ...loadingPanel };
        },
        getDashboardSummarySuccess(state, action: PayloadAction<IAnalyticsSummaryResponse>) {
            state.dashboardSummary = successPanel(action.payload);
        },
        getDashboardSummaryFailure(state) {
            state.dashboardSummary = { ...failurePanel };
        },

        getDashboardTenants(state, _action: PayloadAction<GetDashboardPayload>) {
            state.dashboardTenants = { ...loadingPanel };
        },
        getDashboardTenantsSuccess(state, action: PayloadAction<ITenantRanking[]>) {
            state.dashboardTenants = successPanel(action.payload);
        },
        getDashboardTenantsFailure(state) {
            state.dashboardTenants = { ...failurePanel };
        },

        getDashboardEvents(state, _action: PayloadAction<GetDashboardPayload>) {
            state.dashboardEvents = { ...loadingPanel };
        },
        getDashboardEventsSuccess(state, action: PayloadAction<ITopEvent[]>) {
            state.dashboardEvents = successPanel(action.payload);
        },
        getDashboardEventsFailure(state) {
            state.dashboardEvents = { ...failurePanel };
        },

        getDashboardTrends(state, _action: PayloadAction<GetDashboardPayload>) {
            state.dashboardTrends = { ...loadingPanel };
        },
        getDashboardTrendsSuccess(state, action: PayloadAction<ITrendPoint[]>) {
            state.dashboardTrends = successPanel(action.payload);
        },
        getDashboardTrendsFailure(state) {
            state.dashboardTrends = { ...failurePanel };
        },

        getTenantSummary(state, _action: PayloadAction<GetTenantPayload>) {
            state.tenantSummary = { ...loadingPanel };
        },
        getTenantSummarySuccess(state, action: PayloadAction<IAnalyticsSummaryResponse>) {
            state.tenantSummary = successPanel(action.payload);
        },
        getTenantSummaryFailure(state) {
            state.tenantSummary = { ...failurePanel };
        },

        getTenantEvents(state, _action: PayloadAction<GetTenantPayload>) {
            state.tenantEvents = { ...loadingPanel };
        },
        getTenantEventsSuccess(state, action: PayloadAction<ITopEvent[]>) {
            state.tenantEvents = successPanel(action.payload);
        },
        getTenantEventsFailure(state) {
            state.tenantEvents = { ...failurePanel };
        },

        getTenantTrends(state, _action: PayloadAction<GetTenantPayload>) {
            state.tenantTrends = { ...loadingPanel };
        },
        getTenantTrendsSuccess(state, action: PayloadAction<ITrendPoint[]>) {
            state.tenantTrends = successPanel(action.payload);
        },
        getTenantTrendsFailure(state) {
            state.tenantTrends = { ...failurePanel };
        },

        getEventRecords(state, _action: PayloadAction<GetEventRecordsPayload>) {
            state.eventRecords = { ...loadingPanel };
        },
        getEventRecordsSuccess(state, action: PayloadAction<IEventRecordsResponse>) {
            state.eventRecords = successPanel(action.payload);
        },
        getEventRecordsFailure(state) {
            state.eventRecords = { ...failurePanel };
        },
        // Dropped on page unmount so the next visit never flashes another event's rows
        resetEventRecords(state) {
            state.eventRecords = { ...emptyPanel };
        },
    },
});

export const {
    getAllOrganizationsForAnalytics,
    getAllOrganizationsForAnalyticsSuccess,
    getAllOrganizationsForAnalyticsFailure,
    getDashboardSummary,
    getDashboardSummarySuccess,
    getDashboardSummaryFailure,
    getDashboardTenants,
    getDashboardTenantsSuccess,
    getDashboardTenantsFailure,
    getDashboardEvents,
    getDashboardEventsSuccess,
    getDashboardEventsFailure,
    getDashboardTrends,
    getDashboardTrendsSuccess,
    getDashboardTrendsFailure,
    getTenantSummary,
    getTenantSummarySuccess,
    getTenantSummaryFailure,
    getTenantEvents,
    getTenantEventsSuccess,
    getTenantEventsFailure,
    getTenantTrends,
    getTenantTrendsSuccess,
    getTenantTrendsFailure,
    getEventRecords,
    getEventRecordsSuccess,
    getEventRecordsFailure,
    resetEventRecords,
} = analyticsSlice.actions;

export default analyticsSlice.reducer;
